import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Smartphone, AlertCircle, CheckCircle, Copy, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TwoFactorAuthPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Setup, 2: Verify
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = () => {
    const status = localStorage.getItem(`twoFA_${user?.user_id}`) === 'enabled';
    setIsEnabled(status);
  };

  const handleEnable = async () => {
    setLoading(true);
    setError('');
    
    try {
      // In a real implementation, this would call your backend API
      // to generate a QR code and secret key for 2FA
      
      // Mock QR code generation
      const mockSecret = 'JBSWY3DPEHPK3PXP'; // In production, this comes from backend
      const mockQRCode = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/${encodeURIComponent(user?.email)}?secret=${mockSecret}&issuer=ResumeBuilder`;
      
      setSecret(mockSecret);
      setQrCode(mockQRCode);
      setStep(2);
    } catch (err) {
      setError('Failed to set up two-factor authentication. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (verificationCode.length !== 6) {
        throw new Error('Please enter a 6-digit code');
      }

      // In production, verify the code with your backend
      // For now, we'll just simulate it
      
      // Save 2FA as enabled
      localStorage.setItem(`twoFA_${user?.user_id}`, 'enabled');
      localStorage.setItem(`twoFA_secret_${user?.user_id}`, secret);
      
      setSuccess('Two-factor authentication has been enabled successfully!');
      setIsEnabled(true);
      
      setTimeout(() => {
        navigate('/settings/security');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    setLoading(true);
    try {
      // In production, call your backend API to disable 2FA
      localStorage.removeItem(`twoFA_${user?.user_id}`);
      localStorage.removeItem(`twoFA_secret_${user?.user_id}`);
      
      setSuccess('Two-factor authentication has been disabled.');
      setIsEnabled(false);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('Failed to disable two-factor authentication.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/settings/security"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-4"
          >
            ← Back to Security Settings
          </Link>
          <div className="flex items-center gap-3">
            <div className="bg-primary-600 p-3 rounded-full">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Two-Factor Authentication</h1>
              <p className="text-gray-600 mt-1">
                Add an extra layer of security to your account
              </p>
            </div>
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {isEnabled && step === 1 ? (
            /* 2FA is already enabled */
            <div>
              <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">
                    Two-factor authentication is enabled
                  </h3>
                  <p className="text-sm text-gray-600">
                    Your account is protected with an additional layer of security.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">How it works</h3>
                  <p className="text-sm text-gray-600">
                    When you sign in, you'll be asked to enter a verification code from your authenticator app
                    in addition to your password.
                  </p>
                </div>

                <button
                  onClick={handleDisable}
                  disabled={loading}
                  className="btn btn-secondary text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {loading ? 'Disabling...' : 'Disable Two-Factor Authentication'}
                </button>
              </div>
            </div>
          ) : step === 1 ? (
            /* Step 1: Explain and start setup */
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">What is two-factor authentication?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Two-factor authentication (2FA) adds an extra layer of security to your account. When you sign in,
                  you'll need to provide both your password and a verification code from your phone.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  You'll need an authenticator app
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Download one of these apps on your phone if you haven't already:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Google Authenticator</li>
                  <li>Microsoft Authenticator</li>
                  <li>Authy</li>
                  <li>1Password</li>
                </ul>
              </div>

              <button
                onClick={handleEnable}
                disabled={loading}
                className="btn btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Setting up...' : 'Get Started'}
              </button>
            </div>
          ) : (
            /* Step 2: QR code and verification */
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Scan this QR code</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Open your authenticator app and scan this QR code to add your account.
                </p>
                
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Can't scan the code?</strong> Enter this key manually:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-gray-200 text-sm font-mono">
                      {secret}
                    </code>
                    <button
                      onClick={copySecret}
                      className="btn btn-secondary p-2"
                      title="Copy to clipboard"
                    >
                      {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleVerify} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter the 6-digit code from your app
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Verify and Enable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthPage;
